import { formatRate } from 'core/presenters/laneRatePresenter'

import { type BulkRate } from './BulkRate'
import { type Carrier } from './Carrier'
import { type Quote } from './Quote'
import { type QuoteAccessorial, type AccessorialCharge } from './QuoteAccessorial'
import { type Rate } from './Rate'
import { type RateLine } from './RateLine'
import { type Shipper } from './Shipper'
import { type SpotQuoteStatus } from './SpotQuoteStatus'

export type SpotQuote = {
  carrier: Carrier
  carrierNotes?: string
  notes?: string
  quote: Quote
  shipper: Shipper
  status: SpotQuoteStatus
  uuid: string
  negotiationNote?: string
  underNegotiation: boolean
  accessorials?: Array<QuoteAccessorial>
  ratelines?: Array<RateLine>
  createdAt: Date
} & Rate

export function canReceiveProposals(spotQuote?: SpotQuote) {
  return spotQuote?.quote.canReceiveProposals ?? true
}

export type BookNowItem = {
  uuid: string
  status: 'created' | 'booked' | 'failed'
  statusReason: string | undefined
  rate: string
  shipmentLink: string | undefined
}

export type BookNowStatus = {
  uuid: string
  status: 'created' | 'processing' | 'finished'
  items: Array<BookNowItem>
}

type AccessorialsDict = Record<string, number>

export function canBookNowSpotQuote(spotQuote: SpotQuote | BulkRate, quoteDisabled: boolean) {
  const { quote, bookNowEnabled, status } = spotQuote
  const { targetRate } = quote
  const rateIsPending = status === 'pending'
  return Boolean(targetRate && bookNowEnabled && !quoteDisabled && rateIsPending)
}

// Create a dictionary of draft accessorial charges 'code' as the key
function createAccessorialsDict(draftAccessorials: AccessorialCharge[]): AccessorialsDict {
  return draftAccessorials.reduce((acc, accessorial) => {
    if (accessorial.accessorial?.code) {
      return {
        ...acc,
        [accessorial.accessorial.code]: accessorial.rate,
      }
    }
    return acc
  }, {} as AccessorialsDict)
}

// Compare accessorial charges from spot quote response with draft accessorial charges
function hasRateChanged(
  quoteAccessorials: RateLine[],
  accessorialsDict: AccessorialsDict
): boolean {
  for (const item of quoteAccessorials) {
    const subtype = item.subtype
    const total = formatRate(item.total ?? '', false)

    if (subtype && subtype in accessorialsDict) {
      const rate = formatRate(accessorialsDict[subtype], false)
      if (rate !== total) {
        return true
      }
    }
  }
  return false
}

// Check for any accessorials in the draft that are not in the quote (accessorials with no charges)
// If there are any with non-zero values now, it means this accessorial charge is updating
function hasNewAccessorialCharge(
  quoteAccessorials: RateLine[],
  accessorialsDict: AccessorialsDict
): boolean {
  for (const key in accessorialsDict) {
    if (!quoteAccessorials.some(item => item.subtype === key) && accessorialsDict[key] !== 0) {
      return true
    }
  }
  return false
}

export function checkAccessorialRateChanges(
  draftAccessorials?: AccessorialCharge[],
  quoteAccessorials?: RateLine[]
): boolean {
  if (!draftAccessorials || !quoteAccessorials) return false
  const accessorialsDict = createAccessorialsDict(draftAccessorials)

  return (
    hasRateChanged(quoteAccessorials, accessorialsDict) ||
    hasNewAccessorialCharge(quoteAccessorials, accessorialsDict)
  )
}

export function isRateRejected(status: SpotQuoteStatus) {
  return status === 'rejected'
}
